import { create } from "zustand";
import MaintenanceService from "@/services/maintenance/maintenance.service";

const DEFAULT_POLL_INTERVAL_MS = 60_000;

interface MaintenanceState {
  /** Whether the app should be locked into maintenance mode. */
  isMaintenance: boolean;
  /**
   * `true` until the very first fetch resolves. Used by `App.tsx` to render
   * an initial loading state so we don't flash the app before we know if
   * maintenance is on. Fail-open: if the fetch errors we treat maintenance
   * as off.
   */
  isInitializing: boolean;
  message: string | null;
  /** Internal: the active polling interval handle, if any. */
  pollHandle: ReturnType<typeof setInterval> | null;
}

interface MaintenanceActions {
  /** Refresh maintenance status from the API; fail-open on error. */
  refresh: () => Promise<void>;
  /**
   * Begin polling maintenance status. Safe to call multiple times — the
   * second call is a no-op while polling is already active.
   */
  startPolling: (intervalMs?: number) => void;
  /** Stop polling and clear the interval. */
  stopPolling: () => void;
}

export const useMaintenanceStore = create<
  MaintenanceState & MaintenanceActions
>((set, get) => ({
  isMaintenance: false,
  isInitializing: true,
  message: null,
  pollHandle: null,

  refresh: async () => {
    try {
      const status = await MaintenanceService.getStatus();
      set({
        isMaintenance: status.isEnabled,
        message: status.message,
        isInitializing: false,
      });
    } catch {
      set({ isMaintenance: false, isInitializing: false });
    }
  },

  startPolling: (intervalMs = DEFAULT_POLL_INTERVAL_MS) => {
    const { pollHandle, refresh } = get();
    if (pollHandle !== null) return;

    void refresh();
    const handle = setInterval(() => {
      void refresh();
    }, intervalMs);
    set({ pollHandle: handle });
  },

  stopPolling: () => {
    const { pollHandle } = get();
    if (pollHandle !== null) {
      clearInterval(pollHandle);
      set({ pollHandle: null });
    }
  },
}));
